<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="invoices"
      :loading="loading"
      class="invoice-table py-2 px-4"
      hide-default-footer
      disable-pagination
      item-key="month"
      :expanded.sync="expanded"
      show-expand
      :single-expand="false"
    >
      <!-- Month column -->
      <template v-slot:item.month="{ item }">
        <div class="d-flex align-center">
          <span class="font-weight-medium subtitle-1">{{ formatMonth(item.month) }}</span>
          <v-btn
            x-small
            icon
            class="ml-3"
            :disabled="!hasBreakdown(item)"
            :title="$t('billing.actions.exportCsv')"
            @click.stop="exportMonthCsv(item)"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>
        </div>
      </template>

      <!-- Revenue (Broker) column -->
      <template v-slot:item.brokerRevenue="{ item }">
        <span
          class="font-weight-bold text-h6 font-family-monospace"
          :class="item.brokerRevenue < 0 ? 'error--text' : 'success--text'"
        >R$ {{ formatAmount(item.brokerRevenue) }}</span>
      </template>

      <!-- Revenue (Partner) column -->
      <template v-slot:item.partnerRevenue="{ item }">
        <span class="font-weight-medium text-subtitle-1 font-family-monospace grey--text text--lighten-1">R$ {{ formatAmount(item.partnerRevenue) }}</span>
      </template>

      <!-- Total Revenue column -->
      <template v-slot:item.totalRevenue="{ item }">
        <span class="font-weight-bold text-h6 font-family-monospace">R$ {{ formatAmount((item.brokerRevenue || 0) + (item.partnerRevenue || 0)) }}</span>
      </template>

      <!-- Expanded row displaying cumulative revenue per partner / direct -->
      <template v-slot:expanded-item="{ headers: expandedHeaders, item }">
        <td :colspan="expandedHeaders.length" class="pa-0">
          <div class="py-4 pl-6">
            
            <v-card outlined class="breakdown-card">
              <div v-if="!item.entities || item.entities.length === 0" class="py-4 px-6 grey--text caption">
                <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
                {{ $t('billing.breakdown.noHistory') }}
              </div>
              <v-simple-table v-else class="breakdown-bg" dense>
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-center breakdown-header" style="width: 48px;"></th>
                      <th class="text-left breakdown-header">{{ $t('billing.table.colPartner') }}</th>
                      <th class="text-right breakdown-header">{{ $t('billing.table.colRevenuePartner') }}</th>
                      <th class="text-right breakdown-header">{{ $t('billing.table.colRevenueBroker') }}</th>
                      <th class="text-right breakdown-header">{{ $t('billing.table.colRevenueTotal') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <template v-for="entity in item.entities">
                      <tr
                        :key="entity.cnpj"
                        :class="['breakdown-row', { 'expanded-partner-row': isEntityExpanded(item.month, entity.cnpj) }]"
                      >
                        <td class="breakdown-cell text-center" style="width: 48px;">
                          <v-btn
                            small
                            icon
                            :disabled="!entity.breakdown || entity.breakdown.length === 0"
                            @click="toggleEntity(item.month, entity.cnpj)"
                          >
                            <v-icon small>
                              {{ isEntityExpanded(item.month, entity.cnpj) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
                            </v-icon>
                          </v-btn>
                        </td>
                        <td class="breakdown-cell font-weight-medium">
                          <div class="d-flex align-center">
                            <div class="d-flex flex-column">
                              <span
                                style="max-width: 320px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;"
                                :class="{ 'direct-operations-text': entity.cnpj === brokerCnpj }"
                                :title="entity.name"
                              >
                                {{ entity.name }}
                              </span>
                              <span class="caption grey--text" v-if="entity.cnpj && entity.cnpj !== brokerCnpj">
                                {{ convertToCNPJ(entity.cnpj) }}
                              </span>
                            </div>
                            <v-btn
                              x-small
                              icon
                              class="ml-3"
                              :disabled="!entity.breakdown || entity.breakdown.length === 0"
                              :title="$t('billing.actions.exportCsv')"
                              @click.stop="exportPartnerCsv(item, entity)"
                            >
                              <v-icon small>mdi-download</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td class="breakdown-cell text-right font-family-monospace">
                          {{ entity.cnpj === brokerCnpj ? '-' : formatRevenue(entity.partnerRevenue) }}
                        </td>
                         <td
                           class="breakdown-cell text-right font-family-monospace font-weight-bold"
                           :class="entity.brokerRevenue < 0 ? 'error--text' : 'success--text'"
                         >
                           {{ formatRevenue(entity.brokerRevenue) }}
                         </td>
                        <td class="breakdown-cell text-right font-family-monospace font-weight-bold">
                          {{ formatRevenue((entity.brokerRevenue || 0) + (entity.partnerRevenue || 0)) }}
                        </td>
                      </tr>
                      <tr
                        v-if="isEntityExpanded(item.month, entity.cnpj)"
                        :key="entity.cnpj + '-expanded'"
                        class="breakdown-row no-hover"
                      >
                        <td colspan="5" class="pa-0">
                          <div class="ml-12 mb-8">
                            <invoice-breakdown-table :breakdown="entity.breakdown" flat broker-layout />
                          </div>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </template>
              </v-simple-table>
            </v-card>
          </div>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import convertToCNPJ, { formatDateYM, formatBRLPretty } from "@/utils";
import InvoiceBreakdownTable from "@/components/billing/InvoiceBreakdownTable.vue";
import { generateMonthRevenueCSV, generatePartnerRevenueCSV } from "@/exportRevenueToCSV";

export default {
  name: "RevenueHistoryTable",
  components: {
    InvoiceBreakdownTable,
  },
  props: {
    invoices: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    brokerCnpj: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      expanded: [],
      expandedEntities: [],
    };
  },
  computed: {
    headers() {
      return [
        { text: "", value: "data-table-expand" },
        { text: this.$t('billing.table.colMonth'), value: "month", align: 'start', sortable: false },
        { text: this.$t('billing.table.colRevenuePartner'), value: "partnerRevenue", align: 'end', sortable: false },
        { text: this.$t('billing.table.colRevenueBroker'), value: "brokerRevenue", align: 'end', sortable: false },
        { text: this.$t('billing.table.colRevenueTotal'), value: "totalRevenue", align: 'end', sortable: false },
      ];
    },
  },
  methods: {
    convertToCNPJ,
    formatMonth(monthStr) {
      if (!monthStr) return "";
      const [year, month] = monthStr.split("-");
      const date = new Date(year, parseInt(month) - 1, 1);
      return formatDateYM(date.getTime() / 1000);
    },
    formatAmount(amount) {
      const n = Number(amount);
      if (amount === undefined || amount === null || !Number.isFinite(n)) return "0,00";
      return formatBRLPretty(n);
    },
    formatRevenue(revenue) {
      const n = Number(revenue);
      if (!Number.isFinite(n)) return "-";
      return `R$ ${formatBRLPretty(n)}`;
    },
    hasBreakdown(item) {
      return (item.entities || []).some(entity => entity.breakdown && entity.breakdown.length > 0);
    },
    exportMonthCsv(item) {
      generateMonthRevenueCSV(item.month, item.entities || [], this.brokerCnpj);
    },
    exportPartnerCsv(item, entity) {
      generatePartnerRevenueCSV(item.month, entity, this.brokerCnpj);
    },
    toggleEntity(month, cnpj) {
      const key = `${month}_${cnpj}`;
      const index = this.expandedEntities.indexOf(key);
      if (index === -1) {
        this.expandedEntities.push(key);
      } else {
        this.expandedEntities.splice(index, 1);
      }
    },
    isEntityExpanded(month, cnpj) {
      const key = `${month}_${cnpj}`;
      return this.expandedEntities.includes(key);
    },
  },
};
</script>

<style scoped>
.invoice-table {
  background: transparent !important;
  border: none !important;
}

.breakdown-bg {
  background: #111111 !important;
}

.breakdown-card {
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
}

.flat-right {
  border-radius: 8px 0 0 8px !important;
  border-right: none !important;
}

.border-none {
  border: none !important;
}

.expanded-partner-row .breakdown-cell {
  border-bottom: none !important;
}

.no-hover:hover {
  background: transparent !important;
}

.direct-operations-text {
  font-style: italic !important;
}

.invoice-table ::v-deep tr.v-data-table__expanded__content {
  box-shadow: none !important;
}

.breakdown-title {
  color: #ffffff !important;
  font-weight: 600 !important;
  font-size: 1.1rem !important;
  letter-spacing: 0.5px;
}

.breakdown-header {
  color: #ffffff !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  padding: 10px 12px !important;
  white-space: nowrap;
}

.breakdown-cell {
  font-size: 0.82rem !important;
  color: rgba(255, 255, 255, 0.75) !important;
  padding: 8px 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.breakdown-row:last-child .breakdown-cell {
  border-bottom: none !important;
}
</style>
